/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  phoenixShopHQVPropertyInfoCall,
  phoenixShopHQVPhotogalleryCall,
} from '../../organisms/HotelGalleryTitle/HQVQueries';
import {
  locationDescriptionFilterToDisplayOnHQV,
  resortFeeDescriptionFilterToDisplayOnHQV,
  destinationFeeDescriptionFilterToDisplayOnHQV,
  surchargeOrdinanceCostDescriptionFilterToDisplayOnHQV,
} from '../../constants';
import { logger } from '../../utils';

const { log } = logger({})('hqvFetchers');

export interface HQVPropertyInfoVariables {
  propertyId: string;
  filter?: string;
  descriptionsFilter?: string[];
}

export interface HQVPhotogalleryVariables {
  propertyId: string;
}

export interface HQVFetcherContext {
  requestId?: string;
  locale?: string;
  sessionToken?: string;
}

/**
 * Fetcher function for HQV Property Info data
 * Used by SWR to fetch property information from UXL
 */
export const fetchHQVPropertyInfo = async (
  client: ApolloClient<NormalizedCacheObject>,
  propertyId: string,
  context: HQVFetcherContext = {}
): Promise<any> => {
  const { requestId = '', locale = 'en-US' } = context;

  const descriptionsFilter =
    locale === 'en-US'
      ? [
          locationDescriptionFilterToDisplayOnHQV,
          resortFeeDescriptionFilterToDisplayOnHQV,
          destinationFeeDescriptionFilterToDisplayOnHQV,
          surchargeOrdinanceCostDescriptionFilterToDisplayOnHQV,
        ]
      : ['LOCATION'];

  try {
    const result = await client.query({
      query: phoenixShopHQVPropertyInfoCall,
      variables: {
        propertyId,
        filter: 'PHONE',
        descriptionsFilter,
      },
      context: {
        headers: {
          'x-request-id': requestId,
          'accept-language': locale,
        },
      },
      fetchPolicy: 'cache-first',
    });

    log.debug('fetchHQVPropertyInfo complete:', result.data);
    return result.data;
  } catch (error) {
    log.debug('fetchHQVPropertyInfo error:', error);
    throw error;
  }
};

/**
 * Fetcher function for HQV Photogallery data
 * Used by SWR to fetch photo gallery from UXL
 */
export const fetchHQVPhotogallery = async (
  client: ApolloClient<NormalizedCacheObject>,
  propertyId: string,
  context: HQVFetcherContext = {}
): Promise<any> => {
  const { requestId = '', locale = 'en-US', sessionToken = '' } = context;

  try {
    const result = await client.query({
      query: phoenixShopHQVPhotogalleryCall,
      variables: {
        propertyId,
      },
      context: {
        headers: {
          'x-request-id': requestId,
          'accept-language': locale,
          'x-b3-traceId': sessionToken || 'fallback-token',
          'x-b3-spanId': requestId || `${Date.now()}`,
        },
      },
      fetchPolicy: 'cache-first',
    });

    log.debug('fetchHQVPhotogallery complete:', result.data);
    return result.data;
  } catch (error) {
    log.debug('fetchHQVPhotogallery error:', error);
    throw error;
  }
};
